/**
 * Detail drawer for event/period read-only view (lists, timeline frise, standalone pages).
 */
import React, { useState, useEffect } from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "../config";
import { EventDetailContent, PeriodDetailContent } from "./DetailContent";

export default function DetailDrawer({
  open,
  onClose,
  onOpenDetail,
  onEventDeleted,
  onPeriodDeleted,
  DateIRLToErache,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Event delete dialog: null | { mode: 'simple', event } | { mode: 'periods', event, periodIds, message }
  const [eventDeleteDialog, setEventDeleteDialog] = useState(null);
  const [periodDeleteDialog, setPeriodDeleteDialog] = useState(null);

  useEffect(() => {
    if (!open) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const url =
      open.type === "event"
        ? `${baseUrl}/events/${open.id}/details`
        : `${baseUrl}/periods/${open.id}/details`;
    axios
      .get(url, { withCredentials: true })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message);
        setData(null);
        setLoading(false);
      });
  }, [open]);

  const handleEdit = () => {
    if (!open || !data) return;
    onClose();
    if (open.type === "event") navigate(`/edit-event/${open.id}`);
    else navigate(`/edit-period/${open.id}`);
  };

  const handleDeleteClick = () => {
    if (!open || !data) return;
    if (open.type === "event") {
      setEventDeleteDialog({ mode: "simple", event: data });
    } else {
      setPeriodDeleteDialog({ period: data });
    }
  };

  const handleConfirmEventDelete = async () => {
    if (!eventDeleteDialog?.event) return;
    const { event, mode } = eventDeleteDialog;
    try {
      const config = { withCredentials: true };
      if (mode === "periods") config.params = { confirmDeactivatePeriods: "1" };
      await axios.delete(`${baseUrl}/events/${event.id}`, config);
      setEventDeleteDialog(null);
      onClose();
      onEventDeleted?.();
    } catch (err) {
      if (
        mode === "simple" &&
        err.response?.status === 409 &&
        err.response?.data?.periodIds?.length > 0
      ) {
        setEventDeleteDialog({
          event,
          mode: "periods",
          periodIds: err.response.data.periodIds,
          message:
            err.response.data.message ||
            "Cet événement est lié à des périodes. Elles seront désactivées si vous confirmez.",
        });
        return;
      }
      setEventDeleteDialog(null);
    }
  };

  const handleConfirmPeriodDelete = async () => {
    if (!periodDeleteDialog?.period) return;
    try {
      await axios.delete(`${baseUrl}/periods/${periodDeleteDialog.period.id}`, {
        withCredentials: true,
      });
      setPeriodDeleteDialog(null);
      onClose();
      onPeriodDeleted?.();
    } catch (_) {
      setPeriodDeleteDialog(null);
    }
  };

  const canEdit = data?.canEdit;
  const canDelete = data?.canDelete;

  return (
    <>
      <Drawer
        anchor="right"
        open={!!open}
        onClose={onClose}
        variant="temporary"
        sx={{
          "& .MuiDrawer-paper": {
            width: "33.33%",
            minWidth: 280,
            maxWidth: 480,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1 }}>
          <Typography variant="subtitle1">Détail</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Fermer">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 2, overflow: "auto" }}>
          {loading && <Typography variant="body2">Chargement…</Typography>}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!loading && !error && data && open?.type === "event" && (
            <EventDetailContent
              data={data}
              onOpenDetail={onOpenDetail}
              DateIRLToErache={DateIRLToErache}
            />
          )}
          {!loading && !error && data && open?.type === "period" && (
            <PeriodDetailContent
              data={data}
              onOpenDetail={onOpenDetail}
              DateIRLToErache={DateIRLToErache}
            />
          )}
        </Box>
        {!loading && !error && data && canEdit && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={handleEdit}>
              Éditer
            </Button>
            {canDelete && (
              <Button variant="outlined" color="error" onClick={handleDeleteClick}>
                Supprimer
              </Button>
            )}
          </Box>
        )}
      </Drawer>

      <Dialog open={!!eventDeleteDialog} onClose={() => setEventDeleteDialog(null)}>
        <DialogTitle>
          {eventDeleteDialog?.mode === "periods"
            ? "Événement lié à des périodes"
            : "Supprimer l'évènement"}
        </DialogTitle>
        <DialogContent>
          {eventDeleteDialog?.mode === "periods" ? (
            <>
              <p>{eventDeleteDialog?.message}</p>
              <p>Périodes concernées : {eventDeleteDialog?.periodIds?.length ?? 0}</p>
            </>
          ) : (
            <p>
              Êtes-vous sûr de vouloir supprimer « {eventDeleteDialog?.event?.name} » ? Cette action
              est définitive.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDeleteDialog(null)}>Annuler</Button>
          <Button onClick={handleConfirmEventDelete} variant="contained">
            Confirmer la suppression
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!periodDeleteDialog} onClose={() => setPeriodDeleteDialog(null)}>
        <DialogTitle>Supprimer la période</DialogTitle>
        <DialogContent>
          <p>
            Voulez-vous vraiment supprimer « {periodDeleteDialog?.period?.name} » ? Cette action
            est définitive.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodDeleteDialog(null)}>Annuler</Button>
          <Button onClick={handleConfirmPeriodDelete} color="error" variant="contained">
            Confirmer la suppression
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
